/**
 * Settings → General → "Public URL" (INT-0040): change the tenant subdomain
 * after creation so a typo never means a re-create. Same availability rules
 * as the wizard (API `slug-available`), a confirmation that names the
 * consequences, then PUT /organizations/:id/slug. Old subdomains 301 to the
 * new one (tenant edge), so links already shared keep working.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/clerkShim';
import { useAdminAuth } from '@/auth/AuthProvider';
import useOrganizationStore from '@store/organizationStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TENANT_ROOT_DOMAIN } from '@staty/shared/domains';
import { checkSlug, SlugStatus } from '@/components/CreateOrganization/useSlugAvailability';
import { publicOrgOrigin } from '@/utils/publicSiteUrl';

type AnyDoc = Record<string, any>;
const organizationStore = useOrganizationStore as any;

interface PublicUrlFieldProps {
  org: AnyDoc | null | undefined;
}

export default function PublicUrlField({ org }: PublicUrlFieldProps) {
  const { getToken } = useAuth();
  const { refresh } = useAdminAuth() as { refresh: () => Promise<unknown> };
  const current: string = String(org?.name || '');
  const [slug, setSlug] = useState(current);
  const [status, setStatus] = useState<SlugStatus>('idle');
  const [statusError, setStatusError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSlug(current); }, [current]);

  const unchanged = slug === current;
  useEffect(() => {
    if (unchanged || slug.length < 3) { setStatus('idle'); setStatusError(''); return undefined; }
    let cancelled = false;
    setStatus('checking');
    const t = setTimeout(async () => {
      const r = await checkSlug(slug);
      if (cancelled) return;
      setStatus(r.status);
      setStatusError(r.error || '');
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [slug, unchanged]);

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40));
  };

  const currentOrigin = useMemo(() => (org ? publicOrgOrigin(org, current) : ''), [org, current]);
  // Same env-aware builder as the current line (localhost:3000 locally).
  const nextOrigin = publicOrgOrigin({ name: slug }, slug);
  const canChange = !unchanged && status === 'available' && !saving;

  const confirm = async () => {
    if (!org?._id || saving) return;
    setSaving(true);
    try {
      const change = organizationStore.getState().changeSlug(getToken);
      const updated = await change(org._id, slug);
      const origin = publicOrgOrigin(updated || { name: slug }, slug);
      toast.success(`Your site is now at ${origin}.`);
      setConfirmOpen(false);
      // The session's orgSlug and the sidebar Public Site link read /me.
      try { await refresh(); } catch { /* the change is saved either way */ }
      try { await organizationStore.getState().getOrganizationByName(slug); } catch { /* same */ }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Could not change your URL.';
      toast.error(String(msg));
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const chip = (() => {
    if (unchanged) return null;
    if (status === 'checking') return <span className="text-xs text-muted-foreground">Checking</span>;
    if (status === 'available') return <span className="text-xs font-medium text-success">Available</span>;
    if (status === 'taken') return <span className="text-xs font-medium text-destructive">{statusError || 'This URL is already taken.'}</span>;
    if (slug.length > 0 && slug.length < 3) return <span className="text-xs text-muted-foreground">At least 3 characters</span>;
    return null;
  })();

  return (
    <div className="space-y-2" data-tour="settings-slug">
      <Label htmlFor="public-url-slug">Public URL</Label>
      <div className="flex items-center gap-2">
        <Input
          id="public-url-slug"
          type="text"
          value={slug}
          onChange={onInput}
          autoComplete="off"
          spellCheck={false}
          className="font-mono text-sm"
          aria-describedby="public-url-help"
        />
        <span className="shrink-0 text-sm text-muted-foreground">.{TENANT_ROOT_DOMAIN}</span>
        <Button type="button" variant="outline" size="sm" disabled={!canChange} onClick={() => setConfirmOpen(true)} data-tour="settings-slug-change">
          Change URL
        </Button>
      </div>
      <p id="public-url-help" className="text-xs text-muted-foreground">
        {unchanged ? <>Your site is at <span className="font-mono">{currentOrigin}</span>. Lowercase letters, numbers, and hyphens.</> : chip}
      </p>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change your public URL?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-mono">{currentOrigin}</span>
                  <span className="mx-2 text-muted-foreground">becomes</span>
                  <span className="font-mono">{nextOrigin}</span>
                </p>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                  <li>Links you already shared will redirect to the new address within a few minutes.</li>
                  <li>Players signed in on your site will need to sign in again.</li>
                  <li>You can change your URL once a day.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={saving} data-tour="settings-slug-confirm">
              {saving ? 'Changing' : 'Change URL'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
